import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import _ from 'lodash';
import moment from 'moment-timezone';

import { translateResource } from '../../data/translated_resource/TranslatedResourceActions'
import { ActionButton, Backdrop } from '../../components/Themed.js';
import { getVariations } from '../../data/service/ServiceActions.js';
import {
  createAppointment,
  clearAppointments,
  getClientEstimations,
  generateSequence,
  getPrice,
  checkForConflicts,
  checkForConflictsOpenTimes
} from '../../data/appointment/AppointmentActions.js';
import { updateClientEmployeeEstimations } from '../../data/client/ClientActions.js'
import { getEstimatedEmployee, getEstimationsWithEmployeeAllocations, extractRequiredResourcesBySegmentTemplates, extractDurationsBySegmentTemplates } from '../../data/appointment/AppointmentServices';
import { getEmployeeName } from '../../data/employee/EmployeeReducer.js';
import { getFamilyMemberTerm } from '../../data/app/AppReducer.js';
import { getBookableVariations } from '../../data/service/ServiceReducer.js';
import './Summary.css';
import { t } from '../../locales';

const MIN_ESTIMATIONS_DIFFERENCE_TIME = 30 * 1000; // 30 seconds

class Summary extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      submitting: false,
    };

    this.confirmClick = this.confirmClick.bind(this);
    this.getEstimation = this.getEstimation.bind(this);
    this.getNextAvailableTime = this.getNextAvailableTime.bind(this);
    this.getEmployee = this.getEmployee.bind(this);
    this.goToStaffSelection = this.goToStaffSelection.bind(this);
  }

  async componentDidMount() {
    await this.updateEstimations();
  }

  componentWillUnmount() {
  }

  async refetchClientChildEstimationsAndShowError() {
    const {
      location,
      updateClientEmployeeEstimations
    } = this.props

    const sequences = [
      ...this.getClientSequences(),
      ...this.getChildSequences()
    ]

    await this.updateEstimations();

    this.estimationChanged = true

    const {rawData} = await getEstimationsWithEmployeeAllocations(location.id, sequences);

    updateClientEmployeeEstimations(rawData);

    this.setState({ submitting: false });
  }

  async updateEstimations() {
    const { getVariations } = this.props;
    await Promise.all([getVariations()]);

    const {
      location,
      getClientEstimations,
      clearAppointments,
    } = this.props;

    clearAppointments();

    const sequences = [
      ...this.getClientSequences(),
      ...this.getChildSequences()
    ]

    getClientEstimations(true, location, sequences);
  }

  getClientSequences() {
    const {
      clientVariations,
      clientInformation,
      hasServicesForClient,
      clientEmployee,
    } = this.props;

    const sequences = []
    const clientId = clientInformation.id;

    const selectedClientVariations = clientVariations.filter(
      cv => cv.clientId === clientId && cv.childId == null
    );

    if (hasServicesForClient) {
      sequences.push(
        generateSequence(
          clientId,
          null,
          selectedClientVariations,
          clientEmployee,
          clientEmployee.map(e => ({
            id: _.get(e, 'estimation.real_employee_id', e.employeeId),
          }))
        )
      );
    }

    return sequences
  }

  getChildSequences() {
    const {
      clientVariations,
      clientInformation,
      childrenSelectedForServices,
      clientEmployee,
    } = this.props;

    const sequences = []
    const clientId = clientInformation.id;

    childrenSelectedForServices.forEach(childId => {
      const selectedChildVariations = clientVariations.filter(
        cv => cv.clientId === clientId && cv.childId === childId
      );
      sequences.push(
        generateSequence(
          clientId,
          childId,
          selectedChildVariations,
          clientEmployee,
          clientEmployee.map(e => ({
            id: _.get(e, 'estimation.real_employee_id', e.employeeId),
          }))
        )
      );
    });

    return sequences
  }

  async getClientPrice(employeeId, clientId, childId = null) {
    const { clientVariations, location } = this.props;

    const variationIds = clientVariations.filter(
      e => e.clientId === clientId && e.childId === childId
    );

    const prices = {};

    for (let i = 0; i < variationIds.length; i++) {
      const variationId = variationIds[i].variationId;
      const price = await getPrice(
        location.id,
        variationId,
        clientId,
        employeeId
      );

      prices[variationId] = price;
    }

    return prices;
  }

  checkEstimationDifference(previous, current) {
    const previousTimestamp = moment(previous).valueOf();
    const currentTimestamp = moment(current).valueOf();

    const timeDifference = Math.abs(previousTimestamp - currentTimestamp);

    return timeDifference >= MIN_ESTIMATIONS_DIFFERENCE_TIME;
  }

  checkForEstimationsChanged(latestEstimations) {
    const { 
      bookRealAppointments,
      clientEmployee,
    } = this.props;

    if (
      !latestEstimations ||
      !Array.isArray(latestEstimations) ||
      !latestEstimations.length ||
      !bookRealAppointments
    ) {
      return false;
    }

    if (_.isEqual(latestEstimations, this.props.clientEstimations)) {
      return false;
    }

    return latestEstimations.some((currentEstimation) => {
      const matchingPrevEstimation = this.props.clientEstimations.find(
        (prevEstimation) =>
          prevEstimation.child_id === currentEstimation.child_id
      );
      if (currentEstimation?.chosen_time?.begin_at && matchingPrevEstimation) {
        const timeDifferenceIsSignificant = this.checkEstimationDifference(matchingPrevEstimation.chosen_time.begin_at, currentEstimation.chosen_time.begin_at)
        const employeeIdIsDifferent = currentEstimation.chosen_time.employee_id !== matchingPrevEstimation.chosen_time.employee_id
        // When both are false, an additional check must be made to resolve an edge case.
        if (!(timeDifferenceIsSignificant || employeeIdIsDifferent)) {
          if (!clientEmployee) {
            return true
          }
          const selectedClientEmployeesMatchingChildId = clientEmployee.filter(ce => ce.childId === currentEstimation?.child_id)
          if (!selectedClientEmployeesMatchingChildId) {
            return true
          }
          let selectedClientEmployee = selectedClientEmployeesMatchingChildId[0];
          if (selectedClientEmployee?.estimation?.real_employee_id === currentEstimation.chosen_time.employee_id) {
            return false;
          }
        }
        // If at least one is true, then the expression is true
        return true;
      }

      return false;
    });
  }

  async confirmClick() {
    const {
      location,
      clientInformation,
      clientVariations,
      variations,
      createAppointment,
      hasServicesForClient,
      childrenSelectedForServices,
      bookRealAppointments,
      durations,
      employeeSelection,
    } = this.props;

    this.setState({ submitting: true });

    const clientId = clientInformation.id;
    const promises = [];

    let beginAt = undefined;
    let endAt = undefined;
    let price = undefined;
    let employeeRequested;

    let allEstimationsForAppointmentSequence = {}

    /**
     * (Real Appointments / Waitlist)
     * 
     * ALWAYS request estimations for client_id & all child_id(s) in sequence
     */
    let sequences = [];
    sequences = [
      ...this.getClientSequences(),
      ...this.getChildSequences()
    ]
    const {resultHash, rawData} = await getEstimationsWithEmployeeAllocations(location.id, sequences);
    allEstimationsForAppointmentSequence = resultHash;

    this.estimationChanged = this.checkForEstimationsChanged(rawData);
    this.staffSelectionEnabled = employeeSelection;

    if (this.estimationChanged) {
      this.refetchClientChildEstimationsAndShowError()
      return;
    }

    if (hasServicesForClient) {
      let clientEmployee = this.getEmployee(clientId, null);

      const selectedClientVariations = clientVariations.filter(
        cv => cv.clientId === clientId && cv.childId == null
      );

      if (bookRealAppointments) {
        const clientEmployeeId = clientEmployee?.employeeId || null
        const childId = null // no children when looking at client estimations
        clientEmployee = allEstimationsForAppointmentSequence[`${clientId}_${childId}_${clientEmployeeId}`]
        if (clientEmployee == null) {

          // fallback to original logic
          if (!clientEmployee) {
            clientEmployee = await getEstimatedEmployee(
              location.id,
              selectedClientVariations.map(v => v.variationId)
            );
          }
        } else {
          employeeRequested = true
        }

        if (clientEmployee == null) {
          alert(t('summary.error_fetching_employee'));
          return;
        }

        price = await this.getClientPrice(clientEmployee.employeeId, clientId);
        beginAt = clientEmployee.estimation.begin_at;
        endAt = clientEmployee.estimation.end_at_without_buffer;
        employeeRequested = employeeRequested || !clientEmployee.isNextAvailable;
      } else {
        const clientEmployeeId = clientEmployee?.employeeId || null
        const childId = null // no children when looking at client estimations
        if (!clientEmployee) {
          clientEmployee = allEstimationsForAppointmentSequence[`${clientId}_${childId}_${clientEmployeeId}`]
        }
        // we only need this information for the price
        if (clientEmployee) {
          price = await this.getClientPrice(clientEmployee.employeeId, clientId);
        }
      }
      
      const resourcesBySegmentTemplateIdsMap = extractRequiredResourcesBySegmentTemplates(clientEmployee?.estimation?.segment_fulfillment_tree);
      const durationsBySegmentTemplateIdsMap = extractDurationsBySegmentTemplates(clientEmployee?.estimation?.segment_fulfillment_tree);

      const clientAppointmentData = {
        locationId: location.id,
        clientId: clientId,
        childId: null,
        selectedVariations: selectedClientVariations,
        variations,
        employeeId: clientEmployee ? clientEmployee.employeeId : null,
        bookRealAppointments,
        beginAt,
        endAt,
        price,
        employeeRequested,
        durations,
        resourcesBySegmentTemplateIdsMap,
        durationsBySegmentTemplateIdsMap
      }

      if (bookRealAppointments) {
        let openTimeConflicts = null

        try {
          openTimeConflicts = await checkForConflictsOpenTimes(clientAppointmentData)
        } catch(error) {
          console.error('Error checking for conflicts (Open Times) - client', error)
          // eslint-disable-next-line
          Raven.captureException(error);
        }

        if (openTimeConflicts) {
          this.refetchClientChildEstimationsAndShowError()
          console.log('client: error with Open Time conflicts')
          return
        }

        let conflicts = null

        try {
          conflicts = await checkForConflicts(clientAppointmentData)
        } catch(error) {
          console.error('Error checking for conflicts - client', error)
          // eslint-disable-next-line
          Raven.captureException(error);
        }

        if (conflicts) {
          this.refetchClientChildEstimationsAndShowError()
          console.log('client: error with conflicts')
          return
        }
      }

      const app = createAppointment(clientAppointmentData);

      promises.push(app);
    }

    childrenSelectedForServices.forEach(async childId => {
      let childEmployee = this.getEmployee(clientId, childId);

      const selectedChildVariations = clientVariations.filter(
        cv => cv.clientId === clientId && cv.childId === childId
      );

      if (bookRealAppointments) {
        const childEmployeeId = childEmployee?.employeeId || null
        childEmployee = allEstimationsForAppointmentSequence[`${clientId}_${childId}_${childEmployeeId}`]
        if (childEmployee == null) {
          // fallback to original logic
          if (!childEmployee) {
            childEmployee = await getEstimatedEmployee(
              location.id,
              selectedChildVariations.map(v => v.variationId)
            );
          }
        }

        if (childEmployee == null) {
          alert(t('summary.error_employee'));
          return;
        }

        price = await this.getClientPrice(
          childEmployee.employeeId,
          clientId,
          childId
        );
        beginAt = childEmployee.estimation.begin_at;
        endAt = childEmployee.estimation.end_at_without_buffer;
        employeeRequested = !childEmployee.isNextAvailable;
      } else {
        const childEmployeeId = childEmployee?.employeeId || null
        if (!childEmployee) {
          childEmployee = allEstimationsForAppointmentSequence[`${clientId}_${childId}_${childEmployeeId}`]
        }
        // we only need the price
        if (childEmployee) {
          price = await this.getClientPrice(
            childEmployee.employeeId,
            clientId,
            childId
          );
        }
      }

      const resourcesBySegmentTemplateIdsMap = extractRequiredResourcesBySegmentTemplates(childEmployee?.estimation?.segment_fulfillment_tree);
      const durationsBySegmentTemplateIdsMap = extractDurationsBySegmentTemplates(childEmployee?.estimation?.segment_fulfillment_tree);

      const childAppointmentData = {
        locationId: location.id,
        clientId: clientId,
        childId,
        selectedVariations: selectedChildVariations,
        variations,
        employeeId: childEmployee ? childEmployee.employeeId : null,
        bookRealAppointments,
        beginAt,
        endAt,
        price,
        employeeRequested,
        durations,
        resourcesBySegmentTemplateIdsMap,
        durationsBySegmentTemplateIdsMap
      }

      if (bookRealAppointments) {
        let openTimeConflicts = null

        try {
          openTimeConflicts = await checkForConflictsOpenTimes(childAppointmentData)
        } catch(error) {
          console.error('Error checking for conflicts (Open Times) - child', error)
          // eslint-disable-next-line
          Raven.captureException(error);
        }

        if (openTimeConflicts) {
          this.refetchClientChildEstimationsAndShowError()
          console.log('client: error with conflicts')
          return
        }

        let conflicts = null

        try {
          conflicts = await checkForConflicts(childAppointmentData)
        } catch(error) {
          console.error('Error checking for conflicts - child', error)
          // eslint-disable-next-line
          Raven.captureException(error);
        }

        if (conflicts) {
          this.refetchClientChildEstimationsAndShowError()
          console.log('child: error with conflicts')
          return
        }
      }

      const app = createAppointment(childAppointmentData);

      promises.push(app);
    });

    Promise.all(promises).then(res => {
      if (res?.every(r => r)) {
        this.props.history.push('/confirmation');
      } else {
        this.props.history.push("/");
      }
    });
  }

  getEstimation(clientId, childId, employeeId) {
    const clientEstimations = this.props.clientEstimations.filter(
      ce =>
        ce.client_id === clientId &&
        ce.child_id === childId &&
        ce.employee_id === employeeId
    );

    if (
      clientEstimations &&
      clientEstimations[0] &&
      clientEstimations[0].remaining
    ) {
      const { location } = this.props;
      return moment()
        .tz(location.time_zone)
        .add(clientEstimations[0].remaining, 'seconds')
        .format('h:mm A');
    } else if (!clientEstimations || !clientEstimations[0]) {
      return <i className="fas fa-spinner fa-pulse" />;
    } else {
      return 'N/A';
    }
  }

  getNextAvailableTime(estimation) {
    const { location } = this.props;
    if (estimation && estimation.begin_at) {
      return moment(estimation.begin_at)
        .tz(location.time_zone)
        .format('MMM DD h:mm A');
    } else if (estimation && typeof estimation === 'string') {
      return estimation
    } else if (!estimation) {
      return <i className="fas fa-spinner fa-pulse" />;
    } else {
      return 'N/A';
    }
  }

  getEmployee(clientId, childId) {
    const { employees, clientEmployee } = this.props;

    const ce = clientEmployee.filter(
      ce => ce.clientId === clientId && ce.childId === childId
    )[0];

    if (!ce) return null;

    const employeeId = _.get(ce, 'estimation.real_employee_id', ce.employeeId) || null;
    const estimation = ce.estimation;

    return {
      employeeId,
      isNextAvailable: ce.employeeId === 0,
      name: getEmployeeName(employeeId, employees),
      estimation,
    };
  }

  goToStaffSelection() {
    this.props.history.push('/staffSelection');
  }

  renderFamilyMember(clientId, childId) {
    const {
      clientInformation,
      clientVariations,
      variations,
      serviceProviderTerm,
      familyMemberTerm,
      bookRealAppointments,
      translateResource,
    } = this.props;

    const selectedVariationNames = clientVariations
      .filter(cv => cv.clientId === clientId && cv.childId === childId)
      .map(cv => variations.filter(v => v.id === cv.variationId)[0])
      .map(cv => translateResource('Variation', cv).name);

    let firstName = '';
    let lastName = '';

    if (!childId) {
      firstName = clientInformation.first_name;
      lastName = clientInformation.last_name;
    } else {
      const child = clientInformation.children.filter(c => c.id === childId)[0];
      if (child) {
        firstName = child.first_name;
        lastName = child.last_name;
      }
    }

    const employee = this.getEmployee(clientId, childId);

    const estimation =
      employee && employee.estimation && bookRealAppointments
        ? employee.estimation
        : this.getEstimation(
            clientId,
            childId,
            employee && employee.employeeId
          );

    const nextAvailableTime = bookRealAppointments
      ? this.getNextAvailableTime(estimation)
      : null;

    return (
      <div key={childId} className="summary-item">
        <div className="summary-row">
          <div className="summary-label">
            {childId ? familyMemberTerm : t('summary.customer')}
          </div>
          <div className="summary-text">
            {firstName + (lastName ? ' ' + lastName : '')}
          </div>
        </div>
        {selectedVariationNames.map((svn, i) => (
          <div key={i} className="summary-row">
            <div className="summary-label">
              {i === 0 ? t('summary.services') : ''}
            </div>
            <div className="summary-text">{svn}</div>
          </div>
        ))}
        {employee && employee.name && (
          <div className="summary-row">
            <div className="summary-label">{serviceProviderTerm}</div>
            <div className="summary-text">{employee.name}</div>
          </div>
        )}
        {bookRealAppointments && (
          <div className="summary-row">
            <div className="summary-label">{t('summary.start_time')}</div>
            <div className="summary-text">{nextAvailableTime}</div>
          </div>
        )}
        {!bookRealAppointments && (
          <div className="summary-row">
            <div className="summary-label">{t('summary.est_start_time')}</div>
            <div className="summary-text">{estimation}</div>
          </div>
        )}
      </div>
    );
  }

  render() {
    const {
      clientInformation,
      hasServicesForClient,
      childrenSelectedForServices,
      variations,
    } = this.props;
    if (!variations || variations.length === 0) return null;

    const clientId = clientInformation.id;

    return (
      <React.Fragment>
        <Backdrop id="backdrop" />
        <div className="scroll-container content-container">
          <div className="page-container">
            <div className="summary-title">{t('summary.summary')}</div>
            <div className="content">
              <div className="summary-container">
                {hasServicesForClient &&
                  this.renderFamilyMember(clientId, null)}
                {childrenSelectedForServices.map(childId =>
                  this.renderFamilyMember(clientId, childId)
                )}
              </div>
              <div className="warning-container">
                {this.estimationChanged && <span className="warning-text"> {t('summary.warning-message-one')} <br/> {t('summary.warning-message-two')} </span>}
                {this.estimationChanged && this.staffSelectionEnabled &&
                  <span className="warning-indication-text"> {t('summary.warning-indication-one')} <span className="go-to-staffselection" onClick={this.goToStaffSelection}> {t('summary.warning-indication-two')} </span> {t('summary.warning-indication-three')} </span>}
              </div>
              <ActionButton
                className="summary-confirm-button"
                onClick={this.confirmClick}
                disabled={this.state.submitting}
              >
                {t('summary.check_in')}
                {this.state.submitting && (
                  <React.Fragment>
                    {' '}
                    <i className="fas fa-spinner fa-pulse" />
                  </React.Fragment>
                )}
              </ActionButton>
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

function mapStateToProps(state, ownProps) {
  return {
    bookRealAppointments:
      state.app.businessInfo.company.real_appointments_on_kiosk,
    location: state.app.location,
    variations: getBookableVariations(state.service.variations),
    clientVariations: state.client.clientVariations,
    clientInformation: state.client.clientInformation,
    clientEstimations: state.appointment.clientEstimations,
    hasServicesForClient: state.client.hasServicesForClient,
    childrenSelectedForServices: state.client.childrenSelectedForServices,
    clientEmployee: state.client.clientEmployee,
    employees: state.employee.employees,
    employeeSelection: state.app.businessInfo.company.user_can_select_employee,
    serviceProviderTerm: state.app.serviceProviderTerm,
    familyMemberTerm: getFamilyMemberTerm(state.app.businessInfo),
    appLanguage: state.app.language,
    durations: state.app.durations,
  };
}

export default withRouter(
  connect(mapStateToProps, {
    getVariations,
    createAppointment,
    updateClientEmployeeEstimations,
    getClientEstimations,
    clearAppointments,
    translateResource,
    checkForConflicts,
    checkForConflictsOpenTimes,
  })(Summary)
);
